import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, AlertTriangle } from "lucide-react";

import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../ui/dialog";
import { adminApi, formatApiError } from "../../../context/AuthContext";

/**
 * "Opslaan als klant" voor een opgeslagen ontvanger.
 *
 * Nooit automatisch: eerst duplicaat-detectie op e-mail/telefoon/naam via
 * `POST /admin/customer-recipients/{id}/convert-to-customer`. Bij mogelijke
 * matches geeft de backend 409 met kandidaten; de medewerker kiest dan een
 * bestaande klant of bevestigt expliciet een nieuwe.
 */
export default function RecipientToCustomerAction({
  recipient, onConverted, testIdPrefix = "recipient-to-customer",
}) {
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState(null);

  const convert = async (body) => {
    setBusy(true);
    try {
      const r = await adminApi.post(
        `/admin/customer-recipients/${recipient.id}/convert-to-customer`, body,
      );
      const name = r.data?.customer?.display_name
        || r.data?.customer?.company_name || recipient.name;
      toast.success(r.data?.created
        ? `Klant ${name} aangemaakt`
        : `Ontvanger gekoppeld aan bestaande klant ${name}`);
      setCandidates(null);
      onConverted?.(r.data?.customer);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.probable_duplicate) {
        setCandidates(detail.candidates || []);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  if (recipient.converted_customer_id) {
    return (
      <span className="text-[10px] uppercase font-bold text-emerald-700"
            data-testid={`${testIdPrefix}-done-${recipient.id}`}>
        Is klant
      </span>
    );
  }

  return (
    <>
      <Button
        type="button" size="sm" variant="outline" disabled={busy}
        onClick={(e) => { e.stopPropagation(); convert({}); }}
        className="rounded-none h-7 text-[10px] uppercase font-bold"
        data-testid={`${testIdPrefix}-btn-${recipient.id}`}
      >
        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <UserPlus className="w-3 h-3 mr-1" />}
        Opslaan als klant
      </Button>

      <Dialog open={!!candidates} onOpenChange={(o) => { if (!o) setCandidates(null); }}>
        <DialogContent className="rounded-xl max-w-lg" data-testid={`${testIdPrefix}-duplicates`}>
          <DialogHeader>
            <DialogTitle className="font-chivo font-black text-lg text-[#082c50] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Mogelijk bestaat deze klant al
            </DialogTitle>
            <DialogDescription>
              Kies een bestaande klant om aan te koppelen, of maak bewust een
              nieuwe klant aan. Er wordt nooit stil een dubbele klant aangemaakt.
            </DialogDescription>
          </DialogHeader>

          <ul className="divide-y divide-gray-100 border border-gray-200">
            {(candidates || []).map((c) => (
              <li key={c.id}>
                <button
                  type="button" disabled={busy}
                  onClick={() => convert({ link_customer_id: c.id })}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors"
                  data-testid={`${testIdPrefix}-candidate-${c.id}`}
                >
                  <div className="text-sm font-bold text-[#082c50]">
                    {c.display_name || "—"}
                    {c.customer_number && (
                      <span className="ml-2 text-[10px] font-mono text-gray-500">
                        {c.customer_number}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 truncate">
                    {[c.email, c.phone, c.city].filter(Boolean).join(" · ") || "—"}
                  </div>
                </button>
              </li>
            ))}
          </ul>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <Button variant="outline" onClick={() => setCandidates(null)} disabled={busy}
                    className="rounded-none text-xs">
              Annuleren
            </Button>
            <Button onClick={() => convert({ force_create: true })} disabled={busy}
                    className="rounded-none text-xs bg-[#082c50] text-white"
                    data-testid={`${testIdPrefix}-force-create`}>
              Toch nieuwe klant aanmaken
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
